import  React, { useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { FormTypes, ModalTypes } from 'apps/web_forms/utils/constants';
import { getDraftArray } from 'apps/web_forms/api/api';
import { selectDraftAnswers, selectRepeatGroupFields, selectIsSubmitting } from 'apps/web_forms/hooks/useGlobalSelector';
import { useDraftImages } from 'apps/web_forms/hooks/useDraftImages';
import { getImageInputIdentifiers } from 'apps/web_forms/utils/images';
import { setModal, resetModal, setSuccessfulAction } from 'apps/web_forms/slices/appSlice';
import { setDraftAnswers } from 'apps/web_forms/slices/formSlice';
import { addImageDraftValues } from 'apps/web_forms/slices/multimediaSlice';

import { StyledFormControl } from 'apps/web_forms/components/Form/styles';
import FormElements from 'apps/web_forms/components/Form/FormElements';
import FormPageHeader from 'apps/web_forms/components/FormPageHeader';
import DMButton from 'apps/web_forms/components/commons/DMButton';

const Draft = ({ goBackUrl, selectedForm = {}, loading, form, submit, showNotificationBar, cleanup }) => {
  const formContext = useFormContext();
  const { register, setValue, errors } = formContext;
  const draftId = selectedForm?.identifier;
  const isDraft = selectedForm?.type === FormTypes.DRAFT || selectedForm?.type === FormTypes.DISPATCH_DRAFT;

  const draftAnswers = useSelector(selectDraftAnswers);
  const repeatGroupFields = useSelector(selectRepeatGroupFields);
  const isSubmitting = useSelector(selectIsSubmitting);

  const dispatch = useDispatch();

  useEffect(() => {
    const getDraftArrayFunc = async () => {
      if (Object.keys(form).length > 0) {
        const fullDraft = await getDraftArray(draftId, form);
        const draftAnswerKeys = Object.keys(fullDraft?.draftInitialsAnswers);
        if (draftAnswerKeys) {
          draftAnswerKeys.forEach((key) => {
            if (!Array.isArray(fullDraft?.draftInitialsAnswers[key])) {
              register(key);
              setValue(key, fullDraft?.draftInitialsAnswers[key]);
            }
          });
        }
        dispatch(setDraftAnswers(fullDraft?.draftInitialsAnswers));
      }
    };

    getDraftArrayFunc();
  }, [form]);

  const { draftImages, loading: loadingDraftImages } = useDraftImages({
    draftId, isDraft
  });

   useEffect(() => {
    const inputs = form.inputList;
    if (inputs) {
      const imageMap = new Map();
      draftImages.forEach((draftImage) => {
        /** Adding an if else check  for DEVELOPMENT environment. We will have a base64-data on development.
        For production, the value received from BE will be S3 URL, so it will be image-url*/
        if (draftImage.hasOwnProperty('base64-data')) {
          imageMap.set(
            draftImage['image-identifier'],
            `data:image/jpeg;base64,` + draftImage['base64-data']
          );
        } else {
          imageMap.set(draftImage['image-identifier'], draftImage['image-url']);
        }
      });

      let imageInputIdentifiers = [];
      /** Code adapted to work for images inside GROUP types. It checks for children then it gets the corresponding values */
      let cloneImageInputIdentifiers = getImageInputIdentifiers(inputs);
      if (repeatGroupFields) {
        cloneImageInputIdentifiers.map((imageInputIdentifier) => {
          if (Array.isArray(repeatGroupFields[imageInputIdentifier])) {
            repeatGroupFields[imageInputIdentifier].map((childIdentifier) => {
              imageInputIdentifiers.push(childIdentifier);
            });
          } else {
            imageInputIdentifiers.push(imageInputIdentifier);
          }
        });
      }

      let imageDrafts = {};
      imageInputIdentifiers.forEach((imageInputIdentifier) => {
        const imageIdentifier = draftAnswers[imageInputIdentifier];
        const imageUrl = imageMap.get(imageIdentifier);
        if (imageIdentifier && imageUrl) {
          imageDrafts[imageInputIdentifier] = { imageIdentifier, imageUrl };
        }
      });
      if (Object.entries(imageDrafts).length !== 0) {
        dispatch(addImageDraftValues({ imageDrafts }));
      }
    }
  }, [draftImages, draftAnswers, form.inputList, repeatGroupFields]);

  const openDraftModal = () => {
    dispatch(
      setModal({
        type: ModalTypes.DRAFT_MODAL,
        description: selectedForm?.description,
        extraProps: {
          saveDraft: (description) => {
            dispatch(setSuccessfulAction('saved'));
            submit('draft', description);
            dispatch(resetModal());
            cleanup();
          },
          setDraftModalOpen: () => {
            dispatch(resetModal());
          }
        },
      })
    );
  };

  const submitDraft = useCallback(() => {
    dispatch(setSuccessfulAction('submitted'));
  }, []);

  return !loading && (
    <>
      <FormPageHeader
        sticky
        notificationBarShowing={showNotificationBar}
        title={selectedForm.title}
        description={selectedForm.description}
        goBackUrl={goBackUrl}
        isSubmitting={isSubmitting}
        renderToggleButton={false}
        renderRightButton={() => (
          <DMButton
            disabled={isSubmitting}
            filled
            type='submit'
            callbackMethod={submitDraft}
          >
            Submit
          </DMButton>
        )}
        renderSecondaryRightButton={
          () => (
            <DMButton
              disabled={isSubmitting}
              callbackMethod={() => openDraftModal()}
            >
              Update Draft
            </DMButton>
          )
        }
      />
      <StyledFormControl>
        <FormElements
          elements={form.inputList || []}
          getInput={form.getInput}
          dispatchInitialAnswers={form.dispatchInitialAnswers}
          formContext={formContext}
          formErrors={errors}
        />
      </StyledFormControl>
    </>
  );
};

export default Draft;